/**
 * The design system's own words — "Close", "Try again", "Working…" — in the
 * reader's language.
 *
 * `src/ui` builds on its own (`tsconfig.ui.json`, `rootDir: src/ui`), so it
 * cannot import the app's `@/i18n`. Instead the app hands a translator DOWN
 * through this context (`LocaleProvider` does it), and a component outside the
 * app — a design tool, a preview — gets the English with its `{placeholders}`
 * filled, which is exactly what it rendered before.
 *
 * Call it the same way as the app's `useT()`: `const tr = useUiT();
 * tr('Close')`, with a string LITERAL, so `i18n.test.ts` can find the key and
 * fail when its Hebrew is missing.
 */
import { createContext, useContext } from 'react';

export type UiVars = Readonly<Record<string, string | number>>;
export type UiTranslate = (english: string, vars?: UiVars) => string;

const english: UiTranslate = (text, vars) => (vars
  ? text.replace(/\{(\w+)\}/g, (whole, name: string) => (name in vars ? String(vars[name]) : whole))
  : text);

export const UiTextContext = createContext<UiTranslate>(english);

export function useUiT(): UiTranslate {
  return useContext(UiTextContext);
}
